import { useState } from 'react'
import { ChevronRightIcon, LightBulbIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline'
import type { ToolResult as Result } from '../../lib/messageContent'
import Markdown from './Markdown'
import Spinner from './Spinner'
import ToolResult from './ToolResult'

export type ReasoningPart =
  | { kind: 'text'; text: string }
  | { kind: 'tool'; toolUseId: string; label: string; running: boolean }

type Props = {
  parts: ReasoningPart[]
  /** While a turn streams the box opens itself, then folds away once the reply starts. */
  streaming?: boolean
  results?: Map<string, Result>
}

export default function ReasoningBlock({ parts, streaming = false, results }: Props) {
  const [manual, setManual] = useState<boolean | null>(null)
  const [openResults, setOpenResults] = useState<string[]>([])
  const open = manual ?? streaming

  return (
    <div className="w-full text-[11px] text-stone-500">
      <button
        type="button"
        onClick={() => setManual(!open)}
        className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-left transition hover:bg-stone-200"
      >
        <LightBulbIcon className={`size-3 text-stone-400 ${streaming ? 'animate-pulse' : ''}`} />
        <span className="font-medium">Thinking</span>
        <ChevronRightIcon
          className={`size-3 text-stone-400 transition-transform ${open ? 'rotate-90' : ''} ${
            streaming ? 'animate-pulse text-purple-500' : ''
          }`}
        />
        {streaming && <Spinner className="size-3 border-purple-200 border-t-purple-500" />}
      </button>

      {open && (
        <div className="space-y-1.5 px-1 py-1 text-[11px] leading-relaxed">
          {parts.map((part, index) =>
            part.kind === 'text' ? (
              <Markdown key={index} text={part.text} />
            ) : (
              <div key={index}>
                <div className="flex items-center gap-1 text-[10px] text-stone-500">
                  <WrenchScrewdriverIcon
                    className={`size-2.5 shrink-0 text-stone-400 ${part.running ? 'animate-pulse' : ''}`}
                  />
                  <span>
                    Tool used: <span className="font-medium text-stone-700">{part.label}</span>
                  </span>
                  {results?.has(part.toolUseId) && (
                    <button
                      type="button"
                      onClick={() =>
                        setOpenResults((current) =>
                          current.includes(part.toolUseId)
                            ? current.filter((id) => id !== part.toolUseId)
                            : [...current, part.toolUseId],
                        )
                      }
                      className="flex items-center gap-0.5 text-[10px] text-stone-500 transition hover:text-stone-700"
                    >
                      <ChevronRightIcon
                        className={`size-2.5 transition-transform ${
                          openResults.includes(part.toolUseId) ? 'rotate-90' : ''
                        }`}
                      />
                      <span className="underline decoration-dotted underline-offset-2">Result</span>
                      {results.get(part.toolUseId)?.isError && (
                        <span className="font-medium text-rose-500">(error)</span>
                      )}
                    </button>
                  )}
                </div>
                {openResults.includes(part.toolUseId) && results?.get(part.toolUseId) && (
                  <div className="mt-1">
                    <ToolResult result={results.get(part.toolUseId)!} />
                  </div>
                )}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  )
}
